import React, { useContext } from "react"
import { Alert, Badge, Button, Card, CardBody, Input, Spinner } from "reactstrap"

import LlmInstrContext from "./LlmInstrContext"
import {
  AUTO_APPEND_CAP, DEFAULT_SAMPLE_DAYS, DEFAULT_SAMPLE_SIZE, MAX_INSTR_CHARS,
  SCOPE_QD_REF, SOURCE_COLORS, SOURCE_LABELS,
} from "./constants"

/** Left column: the editor. Stays put while the right-hand side changes, because tuning an
 *  instruction is an edit -> preview -> read -> tweak loop. */
function InstructionEditor({ onRequestSave }) {
  const {
    resolved, draft, setDraft,
    previewing, scope, validateQdRef, validateRecent, cancelPreview,
    canSave, previewMatchesDraft, job, saving,
  } = useContext(LlmInstrContext)

  const instruction = resolved?.instruction
  const location = resolved?.response_context
  if (!instruction || !location) return null

  const source = instruction.source || "NONE"
  const overLimit = draft.length > MAX_INSTR_CHARS
  const cnt = instruction.addnl_llm_instr_cnt
  const atCap = typeof cnt === "number" && cnt >= AUTO_APPEND_CAP

  // The editor goes read-only while a run is in flight: otherwise "Save unlocks only for
  // validated text" would be visibly untrue mid-run, since the text could drift away from
  // what the running job was given.
  const locked = previewing
  const cannotRun = overLimit || !draft.trim()

  return (
    <>
      <Card className="mb-3">
        <CardBody>
          <div className="d-flex justify-content-between align-items-start mb-2">
            <h6 className="mb-0">Additional LLM instruction</h6>
            <Badge color={SOURCE_COLORS[source] || "secondary"} pill>
              {SOURCE_LABELS[source] || source}
            </Badge>
          </div>

          {instruction.save_creates_override && (
            <Alert color="warning" className="py-2 px-2 small mb-2">
              Saving creates a tenant-specific override at
              {` (${location.tenant_shortname || location.tenant_id}, ${location.m_loc_id}, EMAIL)`}.
              The inherited value is left untouched.
            </Alert>
          )}

          {/* The saved value sits above the editor, not folded away below it: the draft is
              almost always an edit OF this text, so it has to be readable while typing. */}
          <div className="mb-2">
            <div className="text-muted" style={{ fontSize: "0.72rem" }}>
              Current instructions
            </div>
            <pre className="bg-light border rounded p-2 mb-0 small"
                 style={{ whiteSpace: "pre-wrap", maxHeight: 150, overflow: "auto" }}>
              {instruction.addnl_llm_instr || "(none)"}
            </pre>
          </div>

          <div className="text-muted" style={{ fontSize: "0.72rem" }}>Your draft</div>
          <Input
            type="textarea"
            rows={7}
            value={draft}
            disabled={locked}
            onChange={e => setDraft(e.target.value)}
            placeholder="Describe the facility's quirk — e.g. “Given a response that starts with PLEASE CONFIRM and asks no other question, treat it as a confirmation. The confirmation code appears as PU#123445 — return it without the PU# prefix.”"
            invalid={overLimit}
          />
          <div className="d-flex justify-content-between mt-1">
            <small className={overLimit ? "text-danger" : "text-muted"}>
              {draft.length} / {MAX_INSTR_CHARS} characters
            </small>
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              disabled={locked}
              onClick={() => setDraft(instruction.addnl_llm_instr || "")}
            >
              ↺ revert to saved
            </button>
          </div>

          {typeof cnt === "number" && (
            <div className="text-muted small mt-2">
              {`addnl_llm_instr_cnt = ${cnt}`}
              {atCap
                ? ` of ${AUTO_APPEND_CAP} — the cap is reached, so manual review will not append here again.`
                : ` of ${AUTO_APPEND_CAP} — shared budget: manual review's confirm-with-data appends count toward it, and so does saving here.`}
            </div>
          )}
        </CardBody>
      </Card>

      <Card className="mb-3">
        <CardBody>
          <h6 className="mb-2">Validate</h6>

          <div className="d-flex flex-wrap align-items-center gap-2">
            {previewing ? (
              <>
                <Button color="primary" size="sm" disabled>
                  <Spinner size="sm" className="me-1" />
                  {scope === SCOPE_QD_REF ? "Validating QD Ref…" : "Validating last 10…"}
                </Button>
                <Button color="link" size="sm" onClick={cancelPreview}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button color="primary" size="sm" disabled={cannotRun}
                        onClick={validateQdRef}>
                  Validate for QD Ref
                </Button>
                <Button color="primary" size="sm" outline disabled={cannotRun}
                        onClick={validateRecent}>
                  Validate for last {DEFAULT_SAMPLE_SIZE}
                </Button>
              </>
            )}
            <Button
              color={canSave ? "success" : "secondary"}
              size="sm"
              disabled={!canSave}
              onClick={onRequestSave}
            >
              {saving ? <Spinner size="sm" /> : "Save instructions"}
            </Button>
          </div>

          <div className="text-muted small mt-2">
            {previewing
              ? "The editor is read-only while a run is in flight."
              : !draft.trim()
              ? "Write a draft instruction to validate it."
              : job && !previewMatchesDraft
              ? "The draft changed since the last validation — run one again before saving."
              : canSave
              ? "Ready to save."
              : "Save unlocks once either validation finishes on this exact text."}
          </div>
          <div className="text-muted mt-1" style={{ fontSize: "0.72rem" }}>
            <strong>QD Ref</strong> replays every stored reply under the tracker you loaded.
            <strong> Last {DEFAULT_SAMPLE_SIZE}</strong> replays this location&apos;s most
            recent {DEFAULT_SAMPLE_SIZE} replies from the past {DEFAULT_SAMPLE_DAYS} days,
            which is where an instruction written for one facility reply shows its collateral
            damage.
          </div>
        </CardBody>
      </Card>

      <Alert color="warning" className="small">
        <strong>Scope.</strong> Saving changes behaviour for <em>every</em> strategy at this
        location. The preview replays each sampled reply under the strategy that originally
        processed it — single, group, grouping key, spreadsheet or cancellation — so what it
        covers depends on what this location has actually received in the window.
      </Alert>
    </>
  )
}

export default InstructionEditor
